<script setup lang="ts">
import {onMounted, ref, watch, onUnmounted} from "vue";
import * as Sentry from "@sentry/vue";
import InputGroup from "primevue/inputgroup";
import InputGroupAddon from "primevue/inputgroupaddon";
import backendCall from "../../services/AxiosService";
import {useToast} from "primevue/usetoast";
import {tempWithinThreshold} from "../../utilities/tempValidation";
import {useConfirm} from "primevue/useconfirm";
import {useGeolocation} from '@vueuse/core'
import {useRequestsStore} from "@/stores/requestStore";
import {useAutomationStore} from "@/stores/automationStore";
import {FUEL_OPTIONS} from "@/utilities/constants";
import { getAllItems, searchItems, getItemQuantityAtLocation, deleteTransaction, getAllLocations, getItemLocationsByItemId, type InventoryLocation, type InventoryItemLocationWithDetails } from '@/services/inventory';
import inventoryService from '@/services/inventory';
import logger from "@/utilities/logger";
import { debounce, DEBOUNCE_DELAY_DEFAULT } from '@/utilities/debounce';
import { submissionTracker } from '@/utilities/submissionTracking';
import Dropdown from 'primevue/dropdown';
import InputNumber from 'primevue/inputnumber';
import Textarea from 'primevue/textarea';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import AutoComplete from 'primevue/autocomplete';
import Badge from 'primevue/badge';
import Checkbox from 'primevue/checkbox';


const toast = useToast();
const requestsStore = useRequestsStore();
const automationStore = useAutomationStore();
const confirm = useConfirm();




const {coords, locatedAt, error, resume, pause} = useGeolocation()



interface Alarm {
  alarm_code: string;
  alarm_name: string;
  manufacturer: string;
}

interface TemperatureData {
  value: number | null | undefined;
  unit: 'C' | 'F' | null | undefined;
  rawInput: string;
}

interface UnitCheckData {
  unitNumber: string;
  returnTemp: TemperatureData;
  billedSetpoint: TemperatureData;
  actualSetpoint: TemperatureData;
  supplyTemp: TemperatureData;
  attachedGenset?: string | null;
  attachedElectricUnit?: string | null; // For clipons - the electric unit they're attached to
  truckNumber: number | null | undefined;
  fuelAdded: number | null;
  startFuelLevel: string | null;
  endFuelLevel: string | null;
  opMode: string | null;
  time: Date;
  initialUnitState: string | null;
  finalUnitState: string | null;
  g1Hours?: number | null;
  g2Hours?: number | null;
  track?: number | null;
  location?: string | null;
  alarms: Alarm[];
  notes: string;
  alarmNotes: string | null;
  eventCode?: string | null;
  unitType: string | null;
  requestCompleted: boolean;
  requestSuccessful: boolean | null;
  couldNotComplete: boolean;
  incompleteReason: string;
  browserCoordinates: {
    accuracy: number | null;
    latitude: number | null;
    longitude: number | null;
  },
  onRvr: boolean
  inDefrost: boolean
  cannotProvideEndFuel: boolean;
  reasonForMissingEndFuel: string;
}

const visible = defineModel<boolean>('visible');
const props = defineProps({
  requestId: Number || null,
  equipmentNumber: String || null,
  equipmentTypeCode: Number || null,
  requestNotes: String || null,
  location: String || null,
});
const emit = defineEmits(['visible-change', 'refresh-requests']);

const unitNumberValidation = ref();

const temperatureDiscrepancies = ref(new Set<string>());

const invalidFields = ref(new Set<string>());

function getInitialUnitCheckData(): UnitCheckData {
  return {
    unitNumber: '',
    billedSetpoint: {value: null, unit: null, rawInput: ''},
    actualSetpoint: {value: null, unit: null, rawInput: ''},
    returnTemp: {value: null, unit: null, rawInput: ''},
    supplyTemp: {value: null, unit: null, rawInput: ''},
    attachedElectricUnit: null,
    truckNumber: null,
    fuelAdded: null,
    startFuelLevel: null,
    endFuelLevel: null,
    time: new Date(),
    alarms: [],
    notes: '',
    alarmNotes: null,
    location: props.location,
    unitType: null,
    requestCompleted: true,
    requestSuccessful: null, // Require explicit selection
    couldNotComplete: false,
    incompleteReason: '',
    onRvr: true,
    inDefrost: false,
    initialUnitState: null,
    finalUnitState: null,
    opMode: null,
    browserCoordinates: {
      accuracy: null,
      latitude: null,
      longitude: null,
    },
    cannotProvideEndFuel: false,
    reasonForMissingEndFuel: '',
  };
}

const unitCheckData = ref<UnitCheckData>(getInitialUnitCheckData());

// Inventory items and transactions
const inventoryItems = ref([]);
const inventoryDialog = ref(false);
const selectedItem = ref(null);
const itemQuantity = ref(1);
const itemNotes = ref('');
const selectedLocation = ref<InventoryLocation | null>(null);
const availableLocations = ref<InventoryLocation[]>([]);
const itemLocations = ref<InventoryItemLocationWithDetails[]>([]);
const inventoryTransactions = ref([]);
const pendingInventoryItems = ref([]);
const inventoryLoading = ref(false);
const isSearchingItems = ref(false);
const isLoadingLocations = ref(false);
const itemSuggestions = ref([]);
let searchTimeout: NodeJS.Timeout;

watch(coords, (newCoords) => {
  if (newCoords.latitude && newCoords.longitude && newCoords.accuracy) {
    unitCheckData.value.browserCoordinates = {
      latitude: newCoords.latitude,
      longitude: newCoords.longitude,
      accuracy: newCoords.accuracy,
    };
  }
});

// Reset couldNotComplete to false when requestSuccessful is set to true
watch(() => unitCheckData.value.requestSuccessful, (newValue) => {
  if (newValue === true) {
    unitCheckData.value.couldNotComplete = false;
  }
});

// Watch for item selection to fetch locations
watch(selectedItem, async (newItem) => {
  if (newItem && newItem.id) {
    await fetchItemLocations(newItem.id);
    // Reset location selection when item changes
    selectedLocation.value = null;
    itemQuantity.value = 1;
  } else {
    itemLocations.value = [];
    selectedLocation.value = null;
  }
});

// Watch for location selection to update available quantity
watch(selectedLocation, (newLocation) => {
  if (newLocation && itemQuantity.value > newLocation.quantity) {
    itemQuantity.value = Math.min(newLocation.quantity, 1);
  }
});

watch(unitCheckData, () => {
  if ((props.equipmentTypeCode == 1 || props.equipmentTypeCode == 3) && unitCheckData.value.opMode === 'off') {
    unitCheckData.value.initialUnitState = 'off';
  }
}, {deep: true});

// Clear end fuel level when cannot provide is checked
watch(() => unitCheckData.value.cannotProvideEndFuel, (cannotProvide) => {
  if (cannotProvide) {
    unitCheckData.value.endFuelLevel = null;
  } else {
    unitCheckData.value.reasonForMissingEndFuel = '';
  }
});


const fuelOptions = ref(FUEL_OPTIONS);


const unitTypeOptions = ref(['KR1', 'KR2', 'KR4', 'KR7', 'KH7']);


// Initialize as an empty array
const alarmSuggestions = ref<Alarm[]>([]);
const allAlarms = ref<Alarm[]>([]);

// Modify fetchAlarmList to update alarmSuggestions
const fetchAlarmList = async () => {
  try {
    const fetchAlarmListCall = await backendCall.get(`units/all-alarms`);
    allAlarms.value = [...fetchAlarmListCall.data.data];
    // Update alarmSuggestions here
    alarmSuggestions.value = [...allAlarms.value];
    // console.log(allAlarms.value);
  } catch (error) {
    console.error("Error fetching alarm list:", error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch alarm list',
      life: 3000
    });
  }
}

const fetchDriverVehicle = async () => {
  try {
    const fetchDriverVehicleCall = await backendCall.get(`user/vehicle-assignment`);
    unitCheckData.value.truckNumber = fetchDriverVehicleCall.data.data?.vehicleAssignment;
    if (!unitCheckData.value.truckNumber) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to fetch your current vehicle. Are you signed in to your vehicle?',
        life: 10000
      });
    }
  } catch (error) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to fetch your current vehicle.',
      life: 3000
    });
  }
}

const fetchInventoryTransactions = async () => {
  try {
    inventoryLoading.value = true;
    const response = await backendCall.get(`inventory/requests/${props.requestId}/transactions`, {
      params: { details: true }
    });
    inventoryTransactions.value = response.data;
    logger.debug('Inventory transactions:', inventoryTransactions.value);
  } catch (err) {
    logger.error('Error fetching inventory transactions:', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load inventory transactions',
      life: 3000
    });
  } finally {
    inventoryLoading.value = false;
  }
};

onMounted(async () => {
  // Load temperature settings for threshold validation
  try {
    await automationStore.fetchTemperatureSettings();
  } catch (error) {
    console.warn('Failed to load temperature settings, using defaults:', error);
  }
  
  await fetchAlarmList();
  await fetchDriverVehicle();
  if (props.requestId) {
    await fetchInventoryTransactions();
  }
  logger.debug('Browser coordinates:', coords);

  // Check for browser support
  const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (SpeechRecognition) {
    recognitionSupported.value = true;
    recognition.value = new SpeechRecognition();
    recognition.value.continuous = false;
    recognition.value.interimResults = true;
    recognition.value.lang = 'en-US';

    recognition.value.onresult = (event: any) => {
      let finalTranscript = '';
      interimTranscript.value = '';
      for (let i = event.resultIndex; i < event.results.length; ++i) {
        if (event.results[i].isFinal) {
          finalTranscript += event.results[i][0].transcript;
        } else {
          interimTranscript.value += event.results[i][0].transcript;
        }
      }
      if (finalTranscript) {
        unitCheckData.value.notes += (unitCheckData.value.notes ? ' ' : '') + finalTranscript.trim();
      }
    };
    recognition.value.onerror = (event: any) => {
      isRecording.value = false;
      interimTranscript.value = '';
      toast.add({
        severity: 'error',
        summary: 'Speech Recognition Error',
        detail: event.error,
        life: 3000
      });
    };
    recognition.value.onend = () => {
      isRecording.value = false;
      interimTranscript.value = '';
    };
  }
});

// Cleanup timeout when component is unmounted
onUnmounted(() => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }
});

// Search inventory items in real-time
const searchInventoryItems = (event: { query: string }) => {
  // Clear previous timeout
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  if (!event.query || event.query.trim().length < 2) {
    itemSuggestions.value = [];
    return;
  }

  isSearchingItems.value = true;

  // Debounce the search by 300ms to avoid too many API calls
  searchTimeout = setTimeout(async () => {
    try {
      console.log('Searching for:', event.query.trim()); // Debug log
      const items = await inventoryService.searchItems(event.query.trim(), true);
      console.log('Search results:', items); // Debug log
      itemSuggestions.value = items;
    } catch (error) {
      console.error('Error searching inventory items:', error);
      itemSuggestions.value = [];
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to search inventory items',
        life: 3000
      });
    } finally {
      isSearchingItems.value = false;
    }
  }, 300);
};

const loadItemLocations = async (item) => {
  if (!item || !item.id) {
    itemLocations.value = [];
    selectedLocation.value = null;
    return;
  }

  try {
    isLoadingLocations.value = true;
    const locations = await inventoryService.getItemLocationsByItemId(item.id, true);
    itemLocations.value = locations.filter(loc => loc.quantity > 0);
    selectedLocation.value = null;
  } catch (error) {
    logger.error('Error loading item locations:', error);
    itemLocations.value = [];
    toast.add({
      severity: 'error',
      summary: 'Location Error',
      detail: 'Failed to load item locations',
      life: 3000
    });
  } finally {
    isLoadingLocations.value = false;
  }
};

// Fetch inventory locations
const fetchInventoryLocations = async () => {
  try {
    const locations = await getAllLocations();
    availableLocations.value = locations;
  } catch (error) {
    console.error('Error fetching inventory locations:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load inventory locations',
      life: 3000
    });
  }
};

// Fetch item locations when item is selected
const fetchItemLocations = async (itemId: number) => {
  try {
    const locations = await getItemLocationsByItemId(itemId, true);
    itemLocations.value = locations as InventoryItemLocationWithDetails[];
  } catch (error) {
    console.error('Error fetching item locations:', error);
    itemLocations.value = [];
  }
};

// Watch for changes in selectedItem
watch(selectedItem, (newItem) => {
  loadItemLocations(newItem);
});

// Watch for item selection to fetch locations
watch(selectedItem, async (newItem) => {
  if (newItem && newItem.id) {
    await fetchItemLocations(newItem.id);
    selectedLocation.value = null;
    itemQuantity.value = 1;
  } else {
    itemLocations.value = [];
    selectedLocation.value = null;
  }
});

const openInventoryDialog = async () => {
  inventoryDialog.value = true;
  selectedItem.value = null;
  selectedLocation.value = null;
  itemQuantity.value = 1;
  itemNotes.value = '';
  itemSuggestions.value = [];
  itemLocations.value = [];

  // Fetch available locations
  await fetchInventoryLocations();
};

const addInventoryItem = async () => {
  if (!selectedItem.value || itemQuantity.value <= 0 || !selectedLocation.value) {
    toast.add({
      severity: 'error',
      summary: 'Invalid Input',
      detail: 'Please select an item, location, and enter a valid quantity',
      life: 3000
    });
    return;
  }

if (!selectedLocation.value) {
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: 'Please select a location for the inventory item',
    life: 3000
  });
  return;
}

// Check if sufficient inventory is available at selected location
const availableAtLocation = selectedLocation.value.quantity || 0;
if (availableAtLocation < itemQuantity.value) {
  toast.add({
    severity: 'error',
    summary: 'Insufficient Inventory',
    detail: `Only ${availableAtLocation} ${selectedItem.value.unit_of_measure} available at ${selectedLocation.value.location?.name || 'selected location'}`,
    life: 3000
  });
  return;
}

try {
  // Get current item details
  const response = await backendCall.get(`inventory/items/${selectedItem.value.id}`);
  const currentItem = response.data;

  // Get quantity available at the selected location
  const locationQuantity = await getItemQuantityAtLocation(selectedItem.value.id, selectedLocation.value.location_id);

  if (locationQuantity <= 0) {
    toast.add({
      severity: 'error',
      summary: 'No Inventory Available',
      detail: `There are no ${currentItem.name} available at ${selectedLocation.value.location?.name || 'this location'}.`,
      life: 5000
    });
    return;
  }

  if (itemQuantity.value > locationQuantity) {
    toast.add({
      severity: 'error',
      summary: 'Insufficient Inventory',
      detail: `Only ${locationQuantity} ${currentItem.unit_of_measure} available at ${selectedLocation.value.location?.name || 'this location'}. Requested: ${itemQuantity.value}`,
      life: 5000
    });
    return;
  }
} catch (error) {
  console.error('Error checking inventory availability:', error);
  toast.add({
    severity: 'error',
    summary: 'Error',
    detail: 'Failed to check inventory availability',
    life: 3000
  });
  return;
}

const pendingItem = {
  id: Date.now(), // Temporary ID for display purposes
  item_id: selectedItem.value.id,
  item_name: selectedItem.value.name,
  part_number: selectedItem.value.part_number,
  sku: selectedItem.value.sku,
  location_id: selectedLocation.value.location_id,
  location_name: selectedLocation.value.location?.name,
  quantity: itemQuantity.value,
  unit_of_measure: selectedItem.value.unit_of_measure,
  notes: itemNotes.value || `Used for request #${props.requestId} from ${selectedLocation.value.location?.name || 'selected location'}`,
  transaction_type: 'issue'
};

  pendingInventoryItems.value.push(pendingItem);

toast.add({
  severity: 'success',
  summary: 'Success',
  detail: `Inventory item added to request from ${selectedLocation.value.location?.name || 'selected location'}`,
  life: 3000
});

  inventoryDialog.value = false;
};

const removeInventoryItem = (item) => {
  confirm.require({
    header: 'Remove Inventory Item',
    message: `Are you sure you want to remove this item?`,
    icon: 'pi pi-exclamation-triangle',
    acceptClass: 'p-button-danger',
    accept: () => {
      // Remove from pending items list
      const index = pendingInventoryItems.value.findIndex(pending => pending.id === item.id);
      if (index > -1) {
        pendingInventoryItems.value.splice(index, 1);
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Inventory item removed from request',
          life: 3000
        });
      }
    }
  });
};

const searchAlarms = (event: { query: string }) => {
  const query = event.query.trim().toLowerCase();

  if (query.length === 0) {
    alarmSuggestions.value = [...allAlarms.value];
  } else {
    alarmSuggestions.value = allAlarms.value.filter((alarm: Alarm) =>
        alarm.alarm_code.toLowerCase().includes(query) || alarm.alarm_name.toLowerCase().includes(query)
    );
  }
};

function validateTemperatureField(field: keyof Pick<UnitCheckData, 'actualSetpoint' | 'returnTemp' | 'supplyTemp'>): boolean {
  const data = unitCheckData.value[field] as TemperatureData;
  if (!data.rawInput) {
    invalidFields.value.delete(field);
    return true; // Consider empty field as valid
  }

  const match = data.rawInput.match(/^(-?\d+(\.\d+)?)(C|F)$/i);
  if (match) {
    const [, numPart, , unitPart] = match;
    const numValue = parseFloat(numPart);
    const unitValue = unitPart.toUpperCase() as 'C' | 'F';

    unitCheckData.value[field] = {
      value: numValue,
      unit: unitValue,
      rawInput: data.rawInput
    };

    invalidFields.value.delete(field);
    checkTemperatureDiscrepancies();
    return true;
  }

  invalidFields.value.add(field);
  toast.add({
    severity: 'error',
    summary: 'Invalid Temperature',
    detail: `${field} must be a number followed by C or F`,
    life: 5000
  });
  return false;
}

// new function to check temperature discrepancies
function checkTemperatureDiscrepancies() {
  const {billedSetpoint, actualSetpoint, returnTemp} = unitCheckData.value;

  temperatureDiscrepancies.value.clear();

  // Use configurable temperature thresholds from automation store
  const SETPOINT_DISCREPANCY_THRESHOLD = automationStore.setpointTolerance;
  const SETPOINT_DISCREPANCY_THRESHOLD_UNIT = 'C';
  const OUT_OF_RANGE_THRESHOLD = automationStore.returnTempTolerance;
  const OUT_OF_RANGE_THRESHOLD_UNIT = 'C'

  // Setpoint discrepancy check disabled - billedSetpoint field not present in UI
  // if (billedSetpoint.value != null && actualSetpoint.value != null) {
  //   const withinThreshold = tempWithinThreshold(
  //       { value: billedSetpoint.value, unit: billedSetpoint.unit as 'C' | 'F' },
  //       { value: actualSetpoint.value, unit: actualSetpoint.unit as 'C' | 'F' },
  //       { value: SETPOINT_DISCREPANCY_THRESHOLD, unit: SETPOINT_DISCREPANCY_THRESHOLD_UNIT as 'C' | 'F' }
  //   );
  //
  //   if (!withinThreshold) {
  //     temperatureDiscrepancies.value.add('setpoint');
  //   }
  // }

  if (actualSetpoint.value != null && returnTemp.value != null) {
    logger.debug('Checking temperature threshold between actual setpoint and return temp');
    const withinThreshold = tempWithinThreshold(
        { value: actualSetpoint.value, unit: actualSetpoint.unit as 'C' | 'F' },
        { value: returnTemp.value, unit: returnTemp.unit as 'C' | 'F' },
        { value: OUT_OF_RANGE_THRESHOLD, unit: OUT_OF_RANGE_THRESHOLD_UNIT as 'C' | 'F' }
    );

    if (!withinThreshold) {
      temperatureDiscrepancies.value.add('return-temp');
      // toast.add({
      //   severity: 'warn',
      //   summary: 'Unit Out Of Range!',
      //   detail: `The Return Temp is not within ${OUT_OF_RANGE_THRESHOLD}°${OUT_OF_RANGE_THRESHOLD_UNIT} of the Setpoint`,
      //   life: 5000
      // });
    }
  }

}

const isSubmitting = ref(false)

// Create a debounced version of the submit function
const debouncedSubmit = debounce(async () => {
  if (isSubmitting.value) return // Check if previous form submission is in progress, if so return early.

  // Check for duplicate submission
  const submissionKey = `request-${props.requestId}`;
  const submissionData = {
    requestId: props.requestId,
    requestSuccessful: unitCheckData.value.requestSuccessful,
    notes: unitCheckData.value.notes,
    incompleteReason: unitCheckData.value.incompleteReason
  };
  
  if (submissionTracker.isDuplicateSubmission(submissionKey, submissionData)) {
    toast.add({
      severity: 'warn',
      summary: 'Duplicate Submission',
      detail: 'This request was just submitted. Please wait a moment before submitting again.',
      life: 3000
    });
    return;
  }

  isSubmitting.value = true

  try {
    // Ensure requestSuccessful is explicitly selected
    if (unitCheckData.value.requestSuccessful === null) {
      toast.add({
        severity: 'error',
        summary: 'Missing Selection',
        detail: 'Please indicate whether the request was successful or not',
        life: 5000
      });
      isSubmitting.value = false;
      return;
    }

    // For clipons, ensure attached electric unit is specified if temperatures are entered
    if (props.equipmentTypeCode === 3 && 
        (unitCheckData.value.actualSetpoint.rawInput || 
         unitCheckData.value.returnTemp.rawInput || 
         unitCheckData.value.supplyTemp.rawInput || 
         unitCheckData.value.inDefrost) && 
        !unitCheckData.value.attachedElectricUnit?.trim()) {
      toast.add({
        severity: 'error',
        summary: 'Missing Information',
        detail: 'Please specify the attached electric unit when recording temperature data for clipons',
        life: 5000
      });
      isSubmitting.value = false;
      return;
    }

    // Validate that requestSuccessful cannot be true if couldNotComplete is true
    if (unitCheckData.value.requestSuccessful && unitCheckData.value.couldNotComplete) {
      toast.add({
        severity: 'error',
        summary: 'Invalid Selection',
        detail: 'A request cannot be marked as successful if it could not be completed',
        life: 5000
      });
      isSubmitting.value = false;
      return;
    }

    // If the request couldn't be completed, check if a reason is provided
    if (unitCheckData.value.couldNotComplete && !unitCheckData.value.incompleteReason.trim()) {
      toast.add({
        severity: 'error',
        summary: 'Missing Information',
        detail: 'Please provide a reason why the request could not be completed.',
        life: 5000
      });
      isSubmitting.value = false;
      return;
    }

    // Validate end fuel level or reason when fuel is added
    if (unitCheckData.value.fuelAdded && unitCheckData.value.fuelAdded > 0) {
      if (!unitCheckData.value.endFuelLevel && !unitCheckData.value.cannotProvideEndFuel) {
        toast.add({
          severity: 'error',
          summary: 'Missing End Fuel Level',
          detail: 'Please provide the end fuel level or indicate why it cannot be provided',
          life: 5000
        });
        isSubmitting.value = false;
        return;
      }

      if (unitCheckData.value.cannotProvideEndFuel && !unitCheckData.value.reasonForMissingEndFuel.trim()) {
        toast.add({
          severity: 'error',
          summary: 'Missing Reason',
          detail: 'Please provide a reason why the end fuel level cannot be recorded',
          life: 5000
        });
        isSubmitting.value = false;
        return;
      }

      if (unitCheckData.value.cannotProvideEndFuel && unitCheckData.value.reasonForMissingEndFuel.trim().length < 10) {
        toast.add({
          severity: 'error',
          summary: 'Insufficient Reason',
          detail: 'Please provide a more detailed reason (minimum 10 characters)',
          life: 5000
        });
        isSubmitting.value = false;
        return;
      }
    }

    // set Initial and final unit state to 'OFF' if op mode is 'OFF' and it is a container or clipon.
    if ((props.equipmentTypeCode == 1 || props.equipmentTypeCode == 3) && unitCheckData?.value?.opMode === 'off') {
      unitCheckData.value.initialUnitState = 'OFF';
    }


    // Only validate temperature fields if the request was completed
    if (!unitCheckData.value.couldNotComplete) {
      // Validate only the fields that exist in the template
      const temperatureFields = ['actualSetpoint', 'returnTemp', 'supplyTemp'] as const;
      for (const field of temperatureFields) {
        if (!validateTemperatureField(field)) {
          isSubmitting.value = false;
          return;
        }
      }
    }

    // At this point, all fields are valid and at least one of returnTemp or supplyTemp is provided
    const dataToSubmit = {
      ...unitCheckData.value,
      // Ensure we're only sending number values for temperatures
      actualSetpoint: unitCheckData.value.actualSetpoint.value !== null ? {
        value: unitCheckData.value.actualSetpoint.value,
        unit: unitCheckData.value.actualSetpoint.unit
      } : undefined,

      returnTemp: unitCheckData.value.returnTemp.value !== null ? {
        value: unitCheckData.value.returnTemp.value,
        unit: unitCheckData.value.returnTemp.unit
      } : undefined,

      supplyTemp: unitCheckData.value.supplyTemp.value !== null ? {
        value: unitCheckData.value.supplyTemp.value,
        unit: unitCheckData.value.supplyTemp.unit
      } : undefined,

      browserCoordinates: unitCheckData.value.browserCoordinates,
      inventoryItems: pendingInventoryItems.value,
      reasonForMissingEndFuel: unitCheckData.value.reasonForMissingEndFuel,
    };

    const submitRequestInfoCall = await backendCall.post(`requests/${props.requestId}/result`, dataToSubmit);

    // Show success toast
    toast.add({
      severity: 'success',
      summary: 'Request Result Submitted',
      detail: `Results for request #${props.requestId} have been successfully submitted.`,
      life: 4000
    });

    // Reset the form
    unitCheckData.value = getInitialUnitCheckData();
    pendingInventoryItems.value = [];

    emit("refresh-requests");
    visible.value = false;
  } catch (e: any) {
    Sentry.captureException(e);
    const errorMessage = e?.response?.data?.error?.message || 'An error occurred while submitting the unit check!';
    toast.add({
      severity: 'error',
      summary: 'Could not submit unit check!',
      detail: errorMessage,
      life: 5000
    });
    throw e;
  } finally {
    isSubmitting.value = false;
  }
}, DEBOUNCE_DELAY_DEFAULT);

// Wrapper function that calls the debounced submit
const submitRequestInfo = () => {
  debouncedSubmit();
};

const handleRequestCompleteChange = () => {
  if (unitCheckData.value.requestCompleted) {
    unitCheckData.value.couldNotComplete = false;
  }
};

const handleCouldNotCompleteChange = () => {
  if (unitCheckData.value.couldNotComplete) {
    // Can't complete a request that couldn't be performed
    unitCheckData.value.requestCompleted = false;
    // Can't succeed with a request that couldn't be performed
    unitCheckData.value.requestSuccessful = false;
  }
};

const completeRequestConfirmation = (requestId: number, event: Event) => {
  confirm.require({
    header: 'Complete Request Without Submitting Info?',
    group: 'defer-info',
    message: 'You will need to submit your info later from the request history page.',
    icon: 'pi pi-info-circle',
    rejectClass: 'p-button-secondary p-button-outlined p-button-sm',
    acceptClass: 'p-button-success p-button-sm',
    rejectLabel: 'Cancel',
    acceptLabel: 'Confirm',
    accept: async () => {
      try {
        const completeRequestCall = await backendCall.post(`requests/${requestId}/complete`)
        visible.value = false;
        requestsStore.fetchRequests()
        toast.add({
          severity: 'success',
          summary: 'Request Complete',
          detail: `Request ${requestId} has been marked as complete!`,
          life: 3000
        });
      } catch (e) {
        toast.add({
          severity: 'error',
          summary: 'Failed',
          detail: `Failed to complete request ${requestId}`,
          life: 3000
        });
      }
    },
    reject: () => {
    }
  });
};

// Speech-to-text for notes
const isRecording = ref(false);
const recognition = ref<any>(null);
const recognitionSupported = ref(false);
const interimTranscript = ref("");

const toggleRecording = () => {
  if (!recognitionSupported.value) {
    toast.add({
      severity: 'warn',
      summary: 'Not Supported',
      detail: 'Speech recognition is not supported in this browser.',
      life: 3000
    });
    return;
  }
  if (isRecording.value) {
    recognition.value.stop();
    isRecording.value = false;
  } else {
    recognition.value.start();
    isRecording.value = true;
    interimTranscript.value = '';
  }
};

</script>

<template>
  <!--v-model:visible="visible"-->
  <Dialog class="w-full max-w-lg md:w-2/3" v-model:visible="visible" appendTo="body" modal
          :header="`Submit Request # ${requestId}`" :draggable="false" :resizable="false" :closable="true">
    <div class="font-bold text-lg">{{ props.equipmentNumber }}</div>
    <div class="p-1 mt-2 border border-solid border-gray-700 rounded-md">
      {{ props.requestNotes }}
    </div>
    <form class="flex flex-col gap-4 mt-2">
      <!--      <div class="flex gap-4">-->
      <!--        <div class="w-full">-->
      <!--          <label for="event_code" class="block mb-1 text-color text-base">Event Code</label>-->
      <!--          <InputGroup>-->
      <!--            <InputGroupAddon>-->
      <!--              EV-->
      <!--            </InputGroupAddon>-->
      <!--            <Select name="event_code" type="text" v-model="unitCheckData.eventCode" class="w-full"-->
      <!--                    id="event-code"-->
      <!--                    :options="eventCodeOptions"/>-->
      <!--          </InputGroup>-->
      <!--        </div>-->
      <!--        <div class="w-full">-->
      <!--          <label for="unit-type" class="block mb-1 text-color text-base">Unit Type</label>-->

      <!--          <Select name="unit_type" :disabled="unitNumberValidation?.dbResponse?.unit_type" type="text"-->
      <!--                  v-model="unitCheckData.unitType" class="w-full"-->
      <!--                  id="unit-type"-->
      <!--                  :options="unitTypeOptions"/>-->
      <!--        </div>-->
      <!--        <div class="w-full text-center">-->
      <!--          <label for="rvr_switch" class="block text-color text-base mb-4">On RVR</label>-->
      <!--          <ToggleSwitch v-model="unitCheckData.onRvr" id="rvr_switch"></ToggleSwitch>-->
      <!--          &lt;!&ndash;          <Checkbox v-model="unitCheckData.onRvr" :binary="true" id="rvr_switch"></Checkbox>&ndash;&gt;-->
      <!--        </div>-->
      <!--      </div>-->
      <div class="flex gap-4">
        <div class="w-1/2">
        <label for="time-input" class="block mb-1 mt-2 text-color text-base">Time</label>
        <DatePicker id="time-input" v-model="unitCheckData.time" showTime hourFormat="24" fluid />
        </div>
        <!-- Attached Electric Unit field for clipons -->
        <div v-if="equipmentTypeCode == 3" class="w-1/2">
          <label for="attached-electric-unit" class="block mb-1 mt-2 text-color text-base">
            Attached Electric Unit
            <span v-if="unitCheckData.actualSetpoint.rawInput || unitCheckData.returnTemp.rawInput || unitCheckData.supplyTemp.rawInput || unitCheckData.inDefrost" class="text-red-500">*</span>
          </label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-link text-lg"></i>
            </InputGroupAddon>
            <InputText 
              id="attached-electric-unit" 
              v-model="unitCheckData.attachedElectricUnit" 
              placeholder="Enter electric unit number"
              class="w-full"
              :class="{ 'p-invalid': equipmentTypeCode == 3 && 
                        (unitCheckData.actualSetpoint.rawInput || unitCheckData.returnTemp.rawInput || unitCheckData.supplyTemp.rawInput || unitCheckData.inDefrost) && 
                        !unitCheckData.attachedElectricUnit?.trim() }"
            />
          </InputGroup>
          <small class="text-orange-600">Temperatures will be recorded against this electric unit</small>
        </div>
      </div>
      <div v-if="equipmentTypeCode == 1 || equipmentTypeCode == 3 || !equipmentTypeCode">
        <!-- Information for clipons about temperature recording -->
        <div v-if="equipmentTypeCode == 3" class="mb-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded">
          <div class="flex items-center gap-2 text-blue-700 dark:text-blue-300">
            <i class="pi pi-info-circle"></i>
            <span class="font-medium">Clipon Temperature Recording</span>
          </div>
          <div class="text-sm text-blue-600 dark:text-blue-400 mt-1">
            Temperature readings will be recorded against the attached electric unit: 
            <span class="font-medium">{{ unitCheckData.attachedElectricUnit || 'Not specified' }}</span>
          </div>
        </div>
        
        <!-- First row: Actual Setpoint and Return Temp -->
        <div class="flex gap-4">
          <div class="w-full">
            <label for="actual-setpoint" class="block mb-1 mt-2 text-color text-base">Actual Setpoint</label>
            <InputGroup>
              <InputGroupAddon>
                <i class="fa-solid fa-temperature-three-quarters"></i>
              </InputGroupAddon>
              <InputText name="actual_setpoint" type="text" v-model="unitCheckData.actualSetpoint.rawInput" class="w-full"
                         id="actual-setpoint" :invalid="invalidFields.has('actualSetpoint')"
                         @blur="validateTemperatureField('actualSetpoint')"/>
            </InputGroup>
            <inline-message v-if="temperatureDiscrepancies.has('setpoint')" severity="warn">Setpoint Discrepancy!
            </inline-message>
          </div>
          <div class="w-full">
            <label for="return-temp" class="block mb-1 mt-2 text-color text-base">Return Temp</label>
            <InputGroup>
              <InputGroupAddon>
                <i class="fa-solid fa-temperature-three-quarters"></i>
              </InputGroupAddon>
              <InputText name="return_temp" type="text" v-model="unitCheckData.returnTemp.rawInput" class="w-full"
                         id="return-temp"
                         :invalid="invalidFields.has('returnTemp')" @blur="validateTemperatureField('returnTemp')"/>
            </InputGroup>
            <inline-message v-if="temperatureDiscrepancies.has('return-temp')" severity="warn">Out Of Range!
            </inline-message>
          </div>
        </div>

        <!-- Second row: Supply Temp and Defrost Toggle -->
        <div class="flex gap-4 mt-3">
          <div class="w-full">
            <label for="supply-temp" class="block mb-1 text-color text-base">Supply Temp</label>
            <InputGroup>
              <InputGroupAddon>
                <i class="fa-solid fa-temperature-three-quarters"></i>
              </InputGroupAddon>
              <InputText name="supply_temp" type="text" v-model="unitCheckData.supplyTemp.rawInput" class="w-full"
                         id="supply-temp"
                         :invalid="invalidFields.has('supplyTemp')" @blur="validateTemperatureField('supplyTemp')"/>
            </InputGroup>
          </div>
          <div class="w-full text-center">
            <label for="defrost_switch" class="block mb-2 text-color text-base">Defrost</label>
            <ToggleSwitch v-model="unitCheckData.inDefrost" id="defrost_switch"></ToggleSwitch>
          </div>
        </div>
      </div>
      <div class="flex gap-4">
        <div v-if="equipmentTypeCode == 1 || equipmentTypeCode == 3 || !equipmentTypeCode" class="w-full">
          <label for="op-mode-dropdown" class="block mb-1 text-color text-base">Op Mode</label>
          <Dropdown v-model="unitCheckData.opMode"
                    :options="['', 'off', 'start/stop', 'continuous']"
                    id="op-mode-dropdown"
                    class="w-full" />
        </div>
        <div class="w-full">
          <label for="location-input" class="block mb-1 text-color text-base">Location / Track:</label>
          <InputText id="location-input" v-model="unitCheckData.location"></InputText>
        </div>
      </div>


      <div v-if="equipmentTypeCode == 2" class="flex gap-4">
        <div class="w-full">
          <label for="g1-hours" class="block mb-1 text-color text-base">G1 Hours</label>
          <InputGroup>
            <InputNumber v-model="unitCheckData.g1Hours" id="g1-hours" :min-fraction-digits="1" name="g1-hours"
                         type="text" class="w-full"/>
            <InputGroupAddon>
              HRS
            </InputGroupAddon>
          </InputGroup>
        </div>
        <div class="w-full">
          <label for="g2-hours" class="block mb-1 text-color text-base">G2 Hours</label>
          <InputGroup>
            <InputNumber v-model="unitCheckData.g2Hours" id="g2-hours" :min-fraction-digits="1" name="g2-hours"
                         type="text" class="w-full"/>
            <InputGroupAddon>
              HRS
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>
      <div class="flex gap-4">
        <div class="w-full">
          <label for="start-fuel" class="block mb-1 mt-2 text-color text-base">Fuel Level</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg"></i>
            </InputGroupAddon>
            <Select name="start_fuel" type="text" v-model="unitCheckData.startFuelLevel" class="w-full"
                    id="start-fuel"
                    :options="fuelOptions"/>
          </InputGroup>
          <small v-if="equipmentTypeCode == 3" class="text-blue-600">Recorded against clipon {{ equipmentNumber }}</small>
        </div>

        <div class="w-full">
          <label for="fuel-added" class="block mb-1 mt-2 text-color text-base">Fuel Added</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg"></i>
            </InputGroupAddon>
            <InputNumber v-model="unitCheckData.fuelAdded" title="Fuel Added"></InputNumber>
            <InputGroupAddon>L</InputGroupAddon>
          </InputGroup>
          <small v-if="equipmentTypeCode == 3" class="text-blue-600">Recorded against clipon {{ equipmentNumber }}</small>
        </div>

      </div>
      <div v-if="unitCheckData.fuelAdded" class="flex justify-center gap-4">
        <div class="w-full">
          <label for="end-fuel" class="block mb-1 mt-2 text-color text-base">
            End Fuel
            <span v-if="unitCheckData.fuelAdded && unitCheckData.fuelAdded > 0" class="text-red-500">*</span>
          </label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-gauge text-lg"></i>
            </InputGroupAddon>
            <Select name="end-fuel" type="text" v-model="unitCheckData.endFuelLevel" class="w-full" id="end-fuel"
                    :options="fuelOptions.toReversed()"
                    :disabled="unitCheckData.cannotProvideEndFuel"/>
          </InputGroup>
        </div>
        <div class="w-full">
          <label for="truck_number" class="block mb-1 mt-2 text-color text-base">Truck #</label>
          <InputGroup>
            <InputGroupAddon>
              <i class="pi pi-truck text-lg"></i>
            </InputGroupAddon>
            <InputNumber v-model="unitCheckData.truckNumber" id="truck_number" title="Truck #"></InputNumber>
          </InputGroup>
        </div>
      </div>

      <!-- Cannot provide end fuel checkbox and reason -->
      <div v-if="unitCheckData.fuelAdded && unitCheckData.fuelAdded > 0" class="flex flex-col gap-3 mt-3">
        <div class="flex items-center gap-2">
          <Checkbox v-model="unitCheckData.cannotProvideEndFuel" inputId="cannot-provide-end-fuel" :binary="true" />
          <label for="cannot-provide-end-fuel" class="cursor-pointer">
            Cannot provide end fuel level
          </label>
        </div>
        
        <div v-if="unitCheckData.cannotProvideEndFuel" class="w-full">
          <label for="reason-missing-end-fuel" class="block mb-1 text-color text-base">
            Reason for missing end fuel level <span class="text-red-500">*</span>
          </label>
          <Textarea
            id="reason-missing-end-fuel"
            v-model="unitCheckData.reasonForMissingEndFuel"
            rows="3"
            class="w-full"
            placeholder="Please explain why the end fuel level cannot be recorded (minimum 10 characters)"
            :maxlength="500"
          />
          <small class="block mt-1 text-surface-500">
            {{ unitCheckData.reasonForMissingEndFuel.length }}/500 characters
          </small>
        </div>
      </div>

      <div class="w-full">
        <label for="alarms" class="block mb-1 mt-2 text-color text-base">Alarms</label>
          <AutoComplete class="w-full mb-1"
                        id="alarms"
                        :multiple="true"
                        v-model="unitCheckData.alarms"
                        :suggestions="alarmSuggestions"
                        placeholder="Search alarms"
                        :option-label="(alarmSuggestion: any) => {return alarmSuggestion.manufacturer + ' - ' + alarmSuggestion.alarm_code + ' - ' + alarmSuggestion.alarm_name}"
                        :force-selection="false"
                        @complete="searchAlarms">
            <template #option="slotProps">
              <div class="flex items-center p-1">
                <i class="pi pi-exclamation-triangle text-orange-500 mr-1"></i>
                <div>
                  <div class="font-medium text-xs">{{ slotProps.option.alarm_code }}</div>
                  <div class="text-xs text-surface-600 dark:text-surface-400">
                    {{ slotProps.option.manufacturer }} - {{ slotProps.option.alarm_name }}
                  </div>
                </div>
              </div>
            </template>
            <template #chip="slotProps">
              <div class="inline-flex items-center px-1 py-0.5 m-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 rounded-full text-xs">
                <span class="mr-1">{{ slotProps.value.alarm_code }}</span>
                <i class="pi pi-times-circle cursor-pointer" @click.stop="unitCheckData.alarms.splice(unitCheckData.alarms.indexOf(slotProps.value), 1)"></i>
              </div>
            </template>
          </AutoComplete>
        <div class="w-full mt-2">
          <label for="alarm-notes" class="block mb-1 text-color text-base">Alarm Notes</label>
          <Textarea name="alarm_notes" v-model="unitCheckData.alarmNotes" class="w-full" id="alarm-notes" placeholder="Enter any notes about the alarms..."/>
        </div>
      </div>
      <Divider v-if="( equipmentTypeCode == 1 || equipmentTypeCode == 3 || equipmentTypeCode == 9 || !equipmentTypeCode ) && unitCheckData.opMode !== 'off'" class="m-0"/>
      <div v-if="( equipmentTypeCode == 1 || equipmentTypeCode == 3 || equipmentTypeCode == 9 || !equipmentTypeCode ) && unitCheckData.opMode !== 'off'" class="flex flex-col gap-3">
        <div class="flex flex-wrap justify-evenly gap-4">
          Initial Unit Status:
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.initialUnitState" inputId="initial-state-dead" name="initial-state"
                         value="DEAD"/>
            <label for="initial-state-dead">Dead</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.initialUnitState" inputId="initial-state-running" name="initial-state"
                         value="RUNNING"/>
            <label for="initial-state-running">Running</label>
          </div>
        </div>
      </div>
      <Divider v-if="( equipmentTypeCode == 1 || equipmentTypeCode == 3 || equipmentTypeCode == 9 || !equipmentTypeCode ) && unitCheckData.opMode !== 'off'" class="m-0"/>
      <div v-if="( equipmentTypeCode == 1 || equipmentTypeCode == 3 || equipmentTypeCode == 9 || !equipmentTypeCode ) && unitCheckData.opMode !== 'off'" class="flex flex-col gap-3">
        <div class="flex flex-wrap justify-evenly gap-4">
          Current Unit Status:
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.finalUnitState" inputId="final-state-dead" name="final-state"
                         value="DEAD"/>
            <label for="final-state-dead">Dead</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.finalUnitState" inputId="final-state-running" name="final-state"
                         value="RUNNING"/>
            <label for="now_running">Running</label>
          </div>
        </div>
      </div>
      <!-- Unit state selection for containers (equipmentTypeCode 1), clipons (equipmentTypeCode 3), and miscellaneous equipment (equipmentTypeCode 9) where op mode is off -->
      <div v-else-if="equipmentTypeCode == 1 || equipmentTypeCode == 3 || equipmentTypeCode == 9 || !equipmentTypeCode" class="flex flex-col gap-3">
        <div class="flex flex-wrap justify-evenly gap-4">
          Current Unit Status:
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.finalUnitState" inputId="final-state-off" name="final-state"
                         value="OFF"/>
            <label for="final-state-off">Off</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.finalUnitState" inputId="final-state-running" name="final-state"
                         value="RUNNING"/>
            <label for="now_running">Running</label>
          </div>
        </div>
      </div>

      <!-- Unit state selection for gensets (equipmentTypeCode 2) -->
      <div v-if="equipmentTypeCode == 2" class="flex flex-col gap-3">
        <div class="flex flex-wrap justify-evenly gap-4">
          Initial Genset Status:
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.initialUnitState" inputId="initial-state-dead-genset" name="initial-state"
                         value="DEAD"/>
            <label for="initial-state-dead-genset">Dead</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.initialUnitState" inputId="initial-state-running-genset" name="initial-state"
                         value="RUNNING"/>
            <label for="initial-state-running-genset">Running</label>
          </div>
        </div>
      </div>
      <Divider v-if="equipmentTypeCode == 2" class="m-0"/>
      <div v-if="equipmentTypeCode == 2" class="flex flex-col gap-3">
        <div class="flex flex-wrap justify-evenly gap-4">
          Current Genset Status:
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.finalUnitState" inputId="final-state-dead-genset" name="final-state"
                         value="DEAD"/>
            <label for="final-state-dead-genset">Dead</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.finalUnitState" inputId="final-state-running-genset" name="final-state"
                         value="RUNNING"/>
            <label for="final-state-running-genset">Running</label>
          </div>
        </div>
      </div>

      <Divider class="m-0"/>
      <div class="flex flex-col gap-3">
        <div class="flex flex-wrap justify-evenly gap-4">
          <span class="font-medium">Request Successful:</span>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.requestSuccessful" inputId="request-successful-yes" name="request-successful"
                         :value="true" :disabled="unitCheckData.couldNotComplete"/>
            <label for="request-successful-yes">Yes</label>
          </div>
          <div class="flex items-center gap-2">
            <RadioButton v-model="unitCheckData.requestSuccessful" inputId="request-successful-no" name="request-successful"
                         :value="false"/>
            <label for="request-successful-no">No</label>
          </div>
        </div>
      </div>

      <div class="flex justify-center gap-4 mt-4">
        <div class="w-1/2 text-center">
          <span class="flex items-center justify-center">
          <label for="request-complete-switch" class="text-color text-base mr-2">Mark Request Complete</label>
          <ToggleSwitch v-model="unitCheckData.requestCompleted" id="request-complete-switch" 
                       @change="handleRequestCompleteChange"></ToggleSwitch>
          </span>
        </div>
        <div class="w-1/2 text-center">
          <span class="flex items-center justify-center">
          <label for="could-not-complete-switch" class="text-color text-base mr-2">Could Not Complete</label>
          <ToggleSwitch v-model="unitCheckData.couldNotComplete" id="could-not-complete-switch"
                       @change="handleCouldNotCompleteChange"></ToggleSwitch>
          </span>
          <div v-if="unitCheckData.couldNotComplete" class="text-sm text-yellow-500 mt-1">
            Use for cases where the unit couldn't be checked (inaccessible, denied track protection, etc.)
          </div>
        </div>
      </div>

      <div v-if="unitCheckData.couldNotComplete">
        <label for="incomplete-reason" class="block mb-1 text-color text-base">Reason for not completing request</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-exclamation-circle mb-2"></i>
          </InputGroupAddon>
          <Textarea name="incomplete-reason" v-model="unitCheckData.incompleteReason" class="w-full" id="incomplete-reason" placeholder="Explain why the request could not be completed"/>
        </InputGroup>
      </div>

      <div>
        <label for="notes" class="block mb-1 text-color text-base">Notes</label>
        <InputGroup>
          <InputGroupAddon>
            <i class="pi pi-file mb-2"></i>
          </InputGroupAddon>
          <Textarea name="notes" v-model="unitCheckData.notes" class="w-full" id="notes"/>
          <Button
            v-if="recognitionSupported"
            :icon="isRecording ? 'pi pi-stop' : 'pi pi-microphone'"
            :class="['p-button-text', isRecording ? 'p-button-danger' : '']"
            @click="toggleRecording"
            :aria-label="isRecording ? 'Stop Recording' : 'Start Recording'"
            style="min-width: 2.5rem;"
          />
        </InputGroup>
        <div v-if="isRecording" class="text-xs text-orange-500 mt-1 flex items-center gap-2">
          <i class="pi pi-spin pi-spinner"></i> Listening... <span v-if="interimTranscript">{{ interimTranscript }}</span>
        </div>
      </div>

      <!-- Inventory Items Section -->
      <div class="mt-4">
        <div class="flex justify-between items-center mb-2">
          <h3 class="text-lg font-semibold">Inventory Items</h3>
          <Button label="Add Item" icon="pi pi-plus" @click="openInventoryDialog" size="small" />
        </div>

        <DataTable 
          :value="pendingInventoryItems"
          stripedRows 
          :rows="3"
          class="p-datatable-sm mb-2"
          v-if="pendingInventoryItems.length > 0"
        >
          <Column field="item_name" header="Item">
            <template #body="{ data }">
              <div>
                <div>{{ data.item_name }}</div>
                <small v-if="data.part_number" class="text-gray-500">Part #: {{ data.part_number }}</small>
                <small v-if="data.sku" class="text-gray-500 block">SKU: {{ data.sku }}</small>
              </div>
            </template>
          </Column>

          <Column header="Location">
            <template #body="{ data }">
              {{ data.location_name }}
            </template>
          </Column>

          <Column header="Quantity">
            <template #body="{ data }">
              {{ data.quantity }} {{ data.unit_of_measure }}
            </template>
          </Column>

          <Column header="Actions" style="width: 80px">
            <template #body="{ data }">
              <Button 
                icon="pi pi-trash" 
                class="p-button-rounded p-button-danger p-button-sm" 
                @click="removeInventoryItem(data)" 
                aria-label="Remove item"
              />
            </template>
          </Column>
        </DataTable>

        <div v-else class="text-center p-2 text-sm text-surface-600 dark:text-surface-400 bg-surface-50 dark:bg-surface-800 rounded mb-2">
          No inventory items added to this request
        </div>

        <Button label="Add Inventory Item" icon="pi pi-plus" @click="openInventoryDialog" class="w-full" />
      </div>
    </form>
    <template #footer>
      <div class="flex justify-between items-center w-full">
        <div>
          <Button label="Defer Info" @click="completeRequestConfirmation(requestId as number, $event)" class="p-button-text"/>
        </div>
        <div class="flex gap-4">
          <Button label="Cancel" @click="visible = false" class="p-button-text"/>
          <Button label="Submit" @click="submitRequestInfo"
                  :loading="isSubmitting"
                  :disabled="isSubmitting ||
                  unitCheckData.requestSuccessful === null ||
                    ((props.equipmentTypeCode === 3 && 
                      (unitCheckData.actualSetpoint.rawInput || unitCheckData.returnTemp.rawInput || unitCheckData.supplyTemp.rawInput || unitCheckData.inDefrost) && 
                      !unitCheckData.attachedElectricUnit?.trim()) ||
                    (!unitCheckData.couldNotComplete && (!unitCheckData.initialUnitState || !unitCheckData.finalUnitState ||
                    (props.equipmentTypeCode !== 2 && props.equipmentTypeCode !== 9 && unitCheckData.opMode !== 'off' && !unitCheckData.actualSetpoint.rawInput && !unitCheckData.returnTemp.rawInput && !unitCheckData.supplyTemp.rawInput && !unitCheckData.notes))) ||
                    (unitCheckData.couldNotComplete && !unitCheckData.incompleteReason) ||
                    (unitCheckData.fuelAdded && unitCheckData.fuelAdded > 0 && !unitCheckData.endFuelLevel && !unitCheckData.cannotProvideEndFuel) ||
                    (unitCheckData.cannotProvideEndFuel && (!unitCheckData.reasonForMissingEndFuel || unitCheckData.reasonForMissingEndFuel.trim().length < 10)))"
                  class="p-button-rounded"/>
        </div>
      </div>
    </template>
  </Dialog>

  <!-- Inventory Dialog -->
  <Dialog 
    v-model:visible="inventoryDialog" 
    header="Add Inventory Item" 
    :style="{ width: '500px' }" 
    modal 
    class="p-fluid"
  >
    <div class="field">
      <label for="item">Item*</label>
      <AutoComplete 
        id="item" 
        v-model="selectedItem" 
        :suggestions="itemSuggestions"
        optionLabel="name" 
        placeholder="Type to search items..."
        :loading="isSearchingItems"
        :minLength="2"
        @complete="searchInventoryItems"
        forceSelection
        class="w-full"
      >
        <template #option="slotProps">
          <div class="flex flex-col">
            <span>{{ slotProps.option.name }}</span>
            <small v-if="slotProps.option.sku" class="text-gray-500">SKU: {{ slotProps.option.sku }}</small>
            <small v-if="slotProps.option.part_number" class="text-gray-500">Part #: {{ slotProps.option.part_number }}</small>
            <small class="text-gray-500">Unit: {{ slotProps.option.unit_of_measure }}</small>
            <small v-if="slotProps.option.current_quantity !== undefined" class="text-blue-600 font-medium">
              Total Available: {{ slotProps.option.current_quantity }} {{ slotProps.option.unit_of_measure }}
            </small>
          </div>
        </template>
      </AutoComplete>
      <small class="text-gray-500 mt-1">Type at least 2 characters to search for items with real-time inventory</small>
    </div>


    <div class="field" v-if="selectedItem">
      <label for="location">Location*</label>
      <Dropdown
        id="location"
        v-model="selectedLocation"
        :options="itemLocations"
        optionLabel="location.name"
        placeholder="Select a location"
        class="w-full"
        :disabled="!selectedItem || itemLocations.length === 0"
      >
        <template #option="slotProps">
          <div class="flex flex-col">
            <span>{{ slotProps.option.location?.name || 'Unknown Location' }}</span>
            <small class="text-blue-600 font-medium">
              Available: {{ slotProps.option.quantity }} {{ selectedItem.unit_of_measure }}
            </small>
          </div>
        </template>

        <template #value="slotProps">
          <div v-if="slotProps.value" class="flex items-center gap-2">
            <span>{{ slotProps.value.location?.name || 'Unknown Location' }}</span>
            <small class="text-blue-600 font-medium">
              ({{ slotProps.value.quantity }} available)
            </small>
          </div>
          <span v-else>Select a location</span>
        </template>
      </Dropdown>
      <div v-if="itemLocations.length === 0 && selectedItem" class="mt-1">
        <small class="text-red-600 font-medium">
          No locations found for this item
        </small>
      </div>
    </div>

    <div class="field">
      <label for="quantity">Quantity*</label>
      <InputNumber 
        id="quantity" 
        v-model="itemQuantity" 
        :min="1" 
        :max="selectedLocation?.quantity || undefined"
        :showButtons="false"
        :disabled="!selectedLocation"
      />
      <div v-if="selectedLocation" class="mt-1">
        <small v-if="selectedLocation.quantity > 0" class="text-green-600 font-medium">
          Available at {{ selectedLocation.location?.name }}: {{ selectedLocation.quantity }} {{ selectedItem.unit_of_measure }}
        </small>
        <small v-else class="text-red-600 font-medium">
          No inventory available at this location
        </small>
      </div>
      <div v-else-if="selectedItem" class="mt-1">
        <small class="text-yellow-600 font-medium">
          Please select a location first
        </small>
      </div>
    </div>

    <div class="field">
      <label for="notes">Notes</label>
      <Textarea id="notes" v-model="itemNotes" rows="3" />
    </div>

    <template #footer>
      <Button 
        label="Cancel" 
        icon="pi pi-times" 
        class="p-button-text" 
        @click="inventoryDialog = false"
      />
      <Button 
        label="Add" 
        icon="pi pi-check" 
        class="p-button-text" 
        :disabled="!selectedItem || !selectedLocation || !itemQuantity || itemQuantity <= 0 || (selectedLocation?.quantity || 0) <= 0"
        @click="addInventoryItem"
      />
    </template>
  </Dialog>
</template>

<style scoped>

</style>
